import * as React from 'react';
import { connect } from 'react-redux';
import { Button } from 'antd';
import { withUserType } from '../withUserType';
import { cn } from '../../utils/ClassNamesUtils';
import { getOscarTheme } from '../../ai_agent/constants/AIAgentTheme';
import { fetchResolutionKnowledge } from '../../thunks/work_orders_thunks';
import {
	FetchResolutionKnowledge,
	ResolutionSummaryWorkOrder,
} from '../../types/resolutionKnowledge';
import { useResolutionSummary } from './useResolutionSummary';

/** Four-point sparkle from the design's "Completion Summary" pill. */
const SparkleIcon = () => (
	<svg viewBox="0 0 10 10" width="10" height="10" aria-hidden="true" className="fill-current">
		<path d="M5 0C5.4 3 7 4.6 10 5C7 5.4 5.4 7 5 10C4.6 7 3 5.4 0 5C3 4.6 4.6 3 5 0Z" />
	</svg>
);

interface WorkOrderResolutionSummaryRestoreProps {
	workOrder: ResolutionSummaryWorkOrder;
	/** False for a supplier only bidding on the work order: they must not see the summary. */
	enabled?: boolean;
	/** Injected: `withUserType` and `mapStateToProps`. */
	userType: string;
	userEmail: string;
	fetchKnowledge: FetchResolutionKnowledge;
}

/**
 * Top of the Overview tab's right column, once the Completion Summary has been dismissed: the
 * pill that brings it back. Renders nothing otherwise.
 */
const WorkOrderResolutionSummaryRestoreBase = ({
	workOrder,
	enabled = true,
	userType,
	userEmail,
	fetchKnowledge,
}: WorkOrderResolutionSummaryRestoreProps) => {
	const { extraction, dismissed, restore } = useResolutionSummary({
		workOrder,
		userEmail,
		enabled,
		fetchKnowledge,
	});
	if (!dismissed || !extraction) return null;

	const theme = getOscarTheme(userType);

	return (
		<div className={cn('mb-m flex rounded px-2.5 py-3.5', theme.surface, theme.dashedStroke)}>
			<Button size="small" shape="round" className={theme.chipBorder} onClick={restore}>
				{/* Icon and label in one span: antd's button CSS loads after Tailwind and would
				    override colour and inline-flex set on the Button itself. */}
				<span className={cn('footnoteregular inline-flex items-center gap-xs', theme.accentText)}>
					<SparkleIcon />
					Completion Summary
				</span>
			</Button>
		</div>
	);
};

const mapStateToProps = (state) => ({
	userEmail: state.session.currentUser && state.session.currentUser.email,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchKnowledge: (workOrderId: number) =>
		dispatch(fetchResolutionKnowledge(ownProps.userType)(workOrderId)),
});

export const WorkOrderResolutionSummaryRestore = withUserType(
	connect(mapStateToProps, mapDispatchToProps)(WorkOrderResolutionSummaryRestoreBase)
);
